import { useState } from 'react';
import { Link, type LinkProps } from '@tanstack/react-router';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
} from '@org/ui';
import { Bell, CheckCheck, ClipboardCheck, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type TeacherNotification = RouterOutputs['notifications']['list']['notifications'][number];

const ICONS: Partial<Record<TeacherNotification['type'], LucideIcon>> = {
  homework_submitted: ClipboardCheck,
};

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['week', 7 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
];

/** "3 hours ago" style text without pulling date-fns into this app. */
function formatRelativeTime(date: Date, language: string): string {
  const formatter = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  for (const [unit, seconds] of RELATIVE_UNITS) {
    if (Math.abs(diffSeconds) >= seconds) {
      return formatter.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return formatter.format(0, 'second');
}

/**
 * Where a notification takes the teacher: the student's page inside the class
 * the work belongs to, or the homework list when the payload lacks the ids.
 */
function getNotificationTarget(notification: TeacherNotification): Pick<LinkProps, 'to' | 'params' | 'search'> {
  const payload = notification.payload as Record<string, unknown>;
  if (notification.type === 'homework_submitted') {
    const classId = typeof payload.classId === 'string' ? payload.classId : undefined;
    const studentId = typeof payload.studentId === 'string' ? payload.studentId : undefined;
    const blockIdx = typeof payload.blockIdx === 'number' && payload.blockIdx >= 0 ? payload.blockIdx : undefined;
    if (classId && studentId) {
      return {
        to: '/classes/$classId/student/$studentId',
        params: { classId, studentId },
        search: { teacherId: undefined, block: blockIdx },
      };
    }
  }
  return { to: '/homework', search: { teacherId: undefined } };
}

function NotificationText({ notification }: { notification: TeacherNotification }) {
  const { t } = useTranslation('admin');
  const payload = notification.payload as Record<string, unknown>;

  if (notification.type === 'homework_submitted') {
    const studentName =
      typeof payload.studentName === 'string' && payload.studentName
        ? payload.studentName
        : t('teacher.notifications.studentFallback');
    const className = typeof payload.className === 'string' && payload.className ? payload.className : null;
    const count = typeof payload.count === 'number' && payload.count > 0 ? payload.count : 1;
    const needsReview = payload.needsReview === true;
    // Presentations (recorded speaking tasks) get their own wording; anything else reads as homework.
    const isPresentation = payload.kind === 'presentation';
    const text = isPresentation
      ? className
        ? t('teacher.notifications.presentationSubmitted', { count, className })
        : t('teacher.notifications.presentationSubmittedNoClass', { count })
      : className
        ? t('teacher.notifications.homeworkSubmitted', { count, className })
        : t('teacher.notifications.homeworkSubmittedNoClass', { count });
    return (
      <>
        <p className="text-sm text-gray-900">
          <span className="font-semibold">{studentName} </span>
          {text}
        </p>
        {needsReview && (
          <span className="mt-1 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-[#cf2e2e]">
            {t('teacher.notifications.needsReview')}
          </span>
        )}
      </>
    );
  }

  return <p className="text-sm text-gray-900">{t('teacher.notifications.newNotification')}</p>;
}

export function NotificationBell() {
  const { t, i18n } = useTranslation('admin');
  const [isOpen, setIsOpen] = useState(false);
  const utils = trpc.useUtils();
  const language = i18n.language?.startsWith('pt') ? 'pt-BR' : 'en-US';

  const { data: unreadData } = trpc.notifications.getUnreadCount.useQuery(
    { audience: 'teacher' },
    { refetchInterval: 30_000 },
  );

  const { data, isLoading } = trpc.notifications.list.useQuery(
    { limit: 20, audience: 'teacher' },
    { enabled: isOpen, refetchInterval: isOpen ? 30_000 : false },
  );

  const invalidate = () => {
    void utils.notifications.list.invalidate();
    void utils.notifications.getUnreadCount.invalidate();
  };

  const markAsRead = trpc.notifications.markAsRead.useMutation({ onSettled: invalidate });
  const markAllAsRead = trpc.notifications.markAllAsRead.useMutation({ onSettled: invalidate });

  const unreadCount = unreadData?.count ?? 0;
  const notifications = data?.notifications ?? [];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            unreadCount > 0
              ? t('teacher.notifications.bellWithUnread', { count: unreadCount })
              : t('teacher.notifications.bell')
          }
          className="relative h-11 w-11 shrink-0 rounded-full border border-rose-100 bg-white/80 text-[#cf2e2e] shadow-sm hover:bg-white"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#cf2e2e] px-1 text-[10px] font-semibold text-white"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(22rem,calc(100vw-2rem))] bg-white/95 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="px-0">{t('teacher.notifications.title')}</DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs text-[#cf2e2e]"
              onClick={() => markAllAsRead.mutate({ audience: 'teacher' })}
              disabled={markAllAsRead.isPending}
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              {t('teacher.notifications.markAllRead')}
            </Button>
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />

        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <Bell className="mx-auto h-8 w-8 opacity-40" />
              <p className="mt-2 text-sm">{t('teacher.notifications.empty')}</p>
            </div>
          ) : (
            <ul className="py-1">
              {notifications.map((notification) => {
                const Icon = ICONS[notification.type] ?? Bell;
                const target = getNotificationTarget(notification);
                const handleOpen = () => {
                  setIsOpen(false);
                  if (!notification.read) {
                    markAsRead.mutate({ notificationId: notification.notificationId });
                  }
                };
                return (
                  <li key={notification.notificationId}>
                    <Link
                      {...target}
                      onClick={handleOpen}
                      className={`flex items-start gap-3 px-3 py-2.5 transition-colors hover:bg-rose-50 ${
                        notification.read ? '' : 'bg-rose-50/60'
                      }`}
                    >
                      <span className="mt-0.5 text-[#cf2e2e]">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <NotificationText notification={notification} />
                        <span className="mt-1 block text-xs text-gray-500">
                          {formatRelativeTime(new Date(notification.createdAt), language)}
                        </span>
                      </span>
                      {!notification.read && (
                        <span aria-hidden="true" className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#cf2e2e]" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
